import { useRef, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Divider,
  IconButton,
  ListItemText,
  MenuItem,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { SPACING } from '@common/theme/spacing'

export interface ViewableSubmission {
  timepointSubmissionId: string
  name: string
  groupName?: string
  isViewable?: boolean
}

export interface ModalityOption {
  name: string
  isViewable?: boolean
}

interface AllTimepointsViewerSelectorProps {
  submissions: ViewableSubmission[]
  modalities: ModalityOption[]
  onOpenViewer: (selectedTimepointSubmissionIds: string[], selectedModalities: string[] | null) => void
  disabled?: boolean
}

const rowSx = {
  py: 0.75,
  px: 2,
  minHeight: 40,
}

const checkboxSx = {
  p: 0.5,
  mr: 1.25,
}

const sectionLabelSx = {
  display: 'block',
  px: 2,
  pt: 1.5,
  pb: 0.75,
  fontSize: 13,
  fontWeight: 600,
  color: 'text.secondary',
  letterSpacing: 0.2,
}

export default function AllTimepointsViewerSelector({
  submissions,
  modalities,
  onOpenViewer,
  disabled,
}: AllTimepointsViewerSelectorProps) {
  const [selectedTimepointIds, setSelectedTimepointIds] = useState<string[] | null>(null)
  const [selectedModalities, setSelectedModalities] = useState<string[] | null>(null)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const open = Boolean(anchorEl)

  const allTimepointIds = submissions.filter((s) => s.isViewable !== false).map((s) => s.timepointSubmissionId)
  const allViewableModalities = modalities.filter((m) => m.isViewable !== false).map((m) => m.name)
  const effectiveTimepoints = selectedTimepointIds ?? allTimepointIds
  const effectiveModalities = selectedModalities ?? allViewableModalities

  const explicitlyEmptyModalities = selectedModalities !== null && selectedModalities.length === 0
  const isDisabled = disabled || effectiveTimepoints.length === 0 || explicitlyEmptyModalities

  const handleTimepointToggle = (id: string) => {
    const current = effectiveTimepoints
    setSelectedTimepointIds(current.includes(id) ? current.filter((x) => x !== id) : [...current, id])
  }

  const handleModalityToggle = (mod: string) => {
    const current = effectiveModalities
    setSelectedModalities(current.includes(mod) ? current.filter((m) => m !== mod) : [...current, mod])
  }

  const allTimepointsSelected =
    allTimepointIds.length > 0 && allTimepointIds.every((id) => effectiveTimepoints.includes(id))
  const someTimepointsSelected = effectiveTimepoints.length > 0 && !allTimepointsSelected

  const allModalitiesSelected =
    allViewableModalities.length > 0 && allViewableModalities.every((m) => effectiveModalities.includes(m))
  const someModalitiesSelected = effectiveModalities.length > 0 && !allModalitiesSelected

  return (
    <Box display="flex" alignItems="flex-start" gap={0.5} flexShrink={0}>
      <Box
        ref={containerRef}
        display="flex"
        alignItems="stretch"
        flexShrink={0}
        sx={{
          border: '1px solid',
          borderColor: 'secondary.dark',
          borderRadius: (theme) => theme.spacing(SPACING.borderRadiusSm),
          overflow: 'hidden',
        }}
      >
        <Button
          disabled={isDisabled}
          onClick={() => onOpenViewer(effectiveTimepoints, selectedModalities)}
          sx={{
            color: 'text.primary',
            border: 'none',
            py: SPACING.spacingMd,
            px: SPACING.spacingLg,
            borderRadius: 0,
            '&:hover': { backgroundColor: 'action.hover' },
          }}
        >
          Open in viewer
        </Button>

        <Divider orientation="vertical" flexItem sx={{ borderColor: 'secondary.dark', my: 1 }} />
        <Tooltip title="Filter timepoints and modalities">
          <span>
            <IconButton
              size="small"
              disabled={disabled || allTimepointIds.length <= 1}
              onClick={(e) => setAnchorEl(open ? null : e.currentTarget)}
              sx={{ borderRadius: 0, px: SPACING.spacingSm }}
            >
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </span>
        </Tooltip>

        <Popover
          anchorEl={anchorEl}
          open={open}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ paper: { sx: { mt: 0.5 } } }}
        >
          <Box display="flex" alignItems="stretch">
            <Box sx={{ minWidth: 260, maxHeight: 460, overflowY: 'auto', pb: 1 }}>
              <Typography sx={sectionLabelSx}>Timepoints</Typography>
              <MenuItem
                onClick={() => setSelectedTimepointIds(allTimepointsSelected ? [] : [...allTimepointIds])}
                sx={rowSx}
              >
                <Checkbox
                  checked={allTimepointsSelected}
                  indeterminate={someTimepointsSelected}
                  disableRipple
                  sx={checkboxSx}
                />
                <ListItemText>Select all</ListItemText>
              </MenuItem>
              {submissions.map((s) => {
                const isViewable = s.isViewable !== false
                return (
                  <MenuItem
                    key={s.timepointSubmissionId}
                    onClick={() => handleTimepointToggle(s.timepointSubmissionId)}
                    disabled={!isViewable}
                    sx={rowSx}
                  >
                    <Checkbox
                      checked={isViewable && effectiveTimepoints.includes(s.timepointSubmissionId)}
                      disabled={!isViewable}
                      disableRipple
                      sx={checkboxSx}
                    />
                    <ListItemText primary={s.name} />
                  </MenuItem>
                )
              })}
            </Box>

            <Divider orientation="vertical" flexItem />

            <Box sx={{ minWidth: 200, maxHeight: 460, overflowY: 'auto', pb: 1 }}>
              <Typography sx={sectionLabelSx}>Modalities</Typography>
              {modalities.length === 0 ? (
                <MenuItem disabled sx={rowSx}>
                  <ListItemText secondary="Loading modalities…" />
                </MenuItem>
              ) : (
                <>
                  <MenuItem
                    onClick={() => setSelectedModalities(allModalitiesSelected ? [] : [...allViewableModalities])}
                    sx={rowSx}
                  >
                    <Checkbox
                      checked={allModalitiesSelected}
                      indeterminate={someModalitiesSelected}
                      disableRipple
                      sx={checkboxSx}
                    />
                    <ListItemText>Select all</ListItemText>
                  </MenuItem>
                  {modalities.map((mod) => {
                    const isViewable = mod.isViewable !== false
                    return (
                      <MenuItem
                        key={mod.name}
                        onClick={() => handleModalityToggle(mod.name)}
                        disabled={!isViewable}
                        sx={rowSx}
                      >
                        <Checkbox
                          checked={isViewable && effectiveModalities.includes(mod.name)}
                          disabled={!isViewable}
                          disableRipple
                          sx={checkboxSx}
                        />
                        <ListItemText primary={mod.name} />
                      </MenuItem>
                    )
                  })}
                </>
              )}
            </Box>
          </Box>
        </Popover>
      </Box>
      <Tooltip title="This button opens DICOM data from submitted visits only, non-DICOM datasets are excluded. Filters are disabled when there is one or no submitted visit.">
        <InfoOutlinedIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
      </Tooltip>
    </Box>
  )
}
